import React, { useState, useEffect } from 'react';
import { TaxConfigurationService } from '../../../services/taxConfigurationService';

const AITaxAssistant = ({ taxRates, jurisdictions, organizationId }) => {
  const [activeFeature, setActiveFeature] = useState('recommendations');
  const [loading, setLoading] = useState(false);
  const [transactionData, setTransactionData] = useState({
    amount: '',
    transactionType: 'sales',
    jurisdictionId: '',
    customerType: 'business'
  });
  const [taxCalculation, setTaxCalculation] = useState(null);
  const [recommendations, setRecommendations] = useState([]);

  const features = [
    { id: 'recommendations', label: 'Smart Recommendations', icon: '🧠' },
    { id: 'calculator', label: 'Tax Calculator', icon: '🧮' },
    { id: 'optimization', label: 'Rate Optimization', icon: '⚡' },
    { id: 'compliance', label: 'Compliance Check', icon: '✅' }
  ];

  const transactionTypes = [
    { value: 'sales', label: 'Sales' },
    { value: 'services', label: 'Services' },
    { value: 'goods', label: 'Goods' },
    { value: 'digital', label: 'Digital Products' },
    { value: 'imports', label: 'Imports' },
    { value: 'exports', label: 'Exports' }
  ];

  const customerTypes = [
    { value: 'business', label: 'Business (B2B)' },
    { value: 'consumer', label: 'Consumer (B2C)' },
    { value: 'government', label: 'Government' },
    { value: 'non_profit', label: 'Non-Profit' }
  ];

  const generateRecommendations = () => {
    const newRecommendations = [];

    // Analyze current tax configuration
    if (jurisdictions?.length === 0) {
      newRecommendations?.push({
        type: 'setup',
        priority: 'high',
        title: 'Set Up Tax Jurisdictions',
        description: 'You haven\'t configured any tax jurisdictions yet. Start by adding the locations where you conduct business.',
        action: 'Add your primary business location as a jurisdiction',
        impact: 'Essential for accurate tax calculations'
      });
    }

    if (taxRates?.length === 0) {
      newRecommendations?.push({
        type: 'setup',
        priority: 'high',
        title: 'Configure Tax Rates',
        description: 'No tax rates have been configured. Add tax rates for your jurisdictions to ensure compliance.',
        action: 'Set up tax rates for each jurisdiction',
        impact: 'Required for tax compliance'
      });
    }

    // Check for expired tax rates
    const expiredRates = taxRates?.filter(rate => 
      rate?.expirationDate && new Date(rate.expirationDate) < new Date()
    );
    
    if (expiredRates?.length > 0) {
      newRecommendations?.push({
        type: 'maintenance',
        priority: 'medium',
        title: 'Update Expired Tax Rates',
        description: `${expiredRates?.length} tax rates have expired and need to be updated.`,
        action: 'Review and update expired tax rates',
        impact: 'Prevents incorrect tax calculations'
      });
    }

    // Check for inactive tax rates that should be active
    const inactiveRates = taxRates?.filter(rate => 
      rate?.status !== 'active' && 
      (!rate?.expirationDate || new Date(rate.expirationDate) > new Date())
    );
    
    if (inactiveRates?.length > 0) {
      newRecommendations?.push({
        type: 'optimization',
        priority: 'low',
        title: 'Activate Valid Tax Rates',
        description: `${inactiveRates?.length} tax rates are inactive but could be activated.`,
        action: 'Review and activate applicable tax rates',
        impact: 'Ensures all applicable taxes are collected'
      });
    }

    // Multi-currency recommendation
    const multiCurrencyEnabled = taxRates?.some(rate => rate?.multiCurrencyEnabled);
    if (!multiCurrencyEnabled && jurisdictions?.some(j => j?.countryCode !== 'US')) {
      newRecommendations?.push({
        type: 'feature',
        priority: 'medium',
        title: 'Enable Multi-Currency Support',
        description: 'You have international jurisdictions but multi-currency tax calculation is not enabled.',
        action: 'Enable multi-currency support for international tax rates',
        impact: 'Improves accuracy for international transactions'
      });
    }

    // Reverse charge recommendation
    const reverseChargeEnabled = taxRates?.some(rate => rate?.reverseChargeEnabled);
    const hasB2BRates = taxRates?.some(rate => rate?.appliesTo?.includes('services'));
    if (!reverseChargeEnabled && hasB2BRates) {
      newRecommendations?.push({
        type: 'compliance',
        priority: 'medium',
        title: 'Consider Reverse Charge Mechanism',
        description: 'For B2B services, you may need to implement reverse charge VAT procedures.',
        action: 'Review reverse charge requirements for your jurisdictions',
        impact: 'Ensures VAT compliance for B2B transactions'
      });
    }

    // Tax rate gaps recommendation
    const salesRates = taxRates?.filter(rate => rate?.appliesTo?.includes('sales'));
    const servicesRates = taxRates?.filter(rate => rate?.appliesTo?.includes('services'));
    
    if (salesRates?.length > servicesRates?.length) {
      newRecommendations?.push({
        type: 'completeness',
        priority: 'low',
        title: 'Configure Service Tax Rates',
        description: 'You have more sales tax rates than service tax rates. Ensure services are properly taxed.',
        action: 'Review and add tax rates for services if applicable',
        impact: 'Ensures comprehensive tax coverage'
      });
    }

    setRecommendations(newRecommendations);
  };

  const calculateTax = async () => {
    if (!transactionData?.amount || !transactionData?.jurisdictionId) {
      return;
    }

    setLoading(true);
    try {
      const totalRate = await TaxConfigurationService?.calculateTotalTaxRate(
        transactionData?.jurisdictionId,
        transactionData?.transactionType
      );

      const applicableRates = await TaxConfigurationService?.getActiveTaxRatesForJurisdiction(
        transactionData?.jurisdictionId
      );

      const amount = parseFloat(transactionData?.amount);
      const taxAmount = amount * totalRate;
      const totalAmount = amount + taxAmount;

      setTaxCalculation({
        baseAmount: amount,
        taxRate: totalRate,
        taxAmount: taxAmount,
        totalAmount: totalAmount,
        applicableRates: applicableRates?.filter(rate => 
          rate?.appliesTo?.includes(transactionData?.transactionType)
        )
      });
    } catch (err) {
      console.error('Error calculating tax:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(amount);
  };

  const formatPercentage = (rate) => {
    return `${(rate * 100)?.toFixed(4)}%`;
  };

  const getPriorityBadge = (priority) => {
    const priorityStyles = {
      high: 'bg-red-100 text-red-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-blue-100 text-blue-800'
    };

    return (
      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${priorityStyles?.[priority]}`}>
        {priority?.charAt(0)?.toUpperCase() + priority?.slice(1)}Priority
              </span>
    );
  };

  const getTypeIcon = (type) => {
    const typeIcons = {
      setup: '⚙️',
      maintenance: '🔧',
      optimization: '⚡',
      feature: '✨',
      compliance: '✅',
      completeness: '📋'
    };
    return typeIcons?.[type] || '💡';
  };

  React.useEffect(() => {
    generateRecommendations();
  }, [taxRates, jurisdictions]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center space-x-3">
          <span className="text-3xl">🤖</span>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">AI Tax Assistant</h2>
            <p className="mt-1 text-sm text-gray-600">
              Get intelligent recommendations and automated tax calculations
            </p>
          </div>
        </div>
      </div>
      {/* Feature Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {features?.map((feature) => (
              <button
                key={feature?.id}
                onClick={() => setActiveFeature(feature?.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center space-x-2 ${
                  activeFeature === feature?.id
                    ? 'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span>{feature?.icon}</span>
                <span>{feature?.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Smart Recommendations */}
          {activeFeature === 'recommendations' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Smart Recommendations</h3>
                <button
                  onClick={generateRecommendations}
                  className="text-sm text-blue-600 hover:text-blue-500 flex items-center space-x-1"
                >
                  <span>🔄</span>
                  <span>Refresh</span>
                </button>
              </div>

              {recommendations?.length === 0 ? (
                <div className="text-center py-8">
                  <span className="text-4xl mb-4 block">🎉</span>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Great job!</h3>
                  <p className="text-sm text-gray-500">
                    Your tax configuration looks good. We'll notify you if any improvements are recommended.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {recommendations?.map((rec, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors duration-200">
                      <div className="flex items-start space-x-3">
                        <span className="text-2xl">{getTypeIcon(rec?.type)}</span>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-medium text-gray-900">{rec?.title}</h4>
                            {getPriorityBadge(rec?.priority)}
                          </div>
                          <p className="mt-1 text-sm text-gray-600">{rec?.description}</p>
                          <div className="mt-3 flex items-center justify-between">
                            <div>
                              <p className="text-xs text-gray-500">
                                <strong>Action:</strong> {rec?.action}
                              </p>
                              <p className="text-xs text-gray-500">
                                <strong>Impact:</strong> {rec?.impact}
                              </p>
                            </div>
                            <button className="text-sm text-blue-600 hover:text-blue-500">
                              Take Action →
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tax Calculator */}
          {activeFeature === 'calculator' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900">Tax Calculator</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-2">
                      Transaction Amount
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      id="amount"
                      value={transactionData?.amount}
                      onChange={(e) => setTransactionData(prev => ({ ...prev, amount: e?.target?.value }))}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <label htmlFor="transactionType" className="block text-sm font-medium text-gray-700 mb-2">
                      Transaction Type
                    </label>
                    <select
                      id="transactionType"
                      value={transactionData?.transactionType}
                      onChange={(e) => setTransactionData(prev => ({ ...prev, transactionType: e?.target?.value }))}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {transactionTypes?.map((type) => (
                        <option key={type?.value} value={type?.value}>
                          {type?.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="jurisdictionId" className="block text-sm font-medium text-gray-700 mb-2">
                      Jurisdiction
                    </label>
                    <select
                      id="jurisdictionId"
                      value={transactionData?.jurisdictionId}
                      onChange={(e) => setTransactionData(prev => ({ ...prev, jurisdictionId: e?.target?.value }))}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select a jurisdiction</option>
                      {jurisdictions?.map((jurisdiction) => (
                        <option key={jurisdiction?.id} value={jurisdiction?.id}>
                          {jurisdiction?.name} ({jurisdiction?.jurisdictionType})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="customerType" className="block text-sm font-medium text-gray-700 mb-2">
                      Customer Type
                    </label>
                    <select
                      id="customerType"
                      value={transactionData?.customerType}
                      onChange={(e) => setTransactionData(prev => ({ ...prev, customerType: e?.target?.value }))}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {customerTypes?.map((type) => (
                        <option key={type?.value} value={type?.value}>
                          {type?.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button
                    onClick={calculateTax}
                    disabled={loading || !transactionData?.amount || !transactionData?.jurisdictionId}
                    className={`w-full py-2 px-4 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                      loading || !transactionData?.amount || !transactionData?.jurisdictionId
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed' :'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {loading ? 'Calculating...' : 'Calculate Tax'}
                  </button>
                </div>

                <div>
                  {taxCalculation && (
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h4 className="text-lg font-medium text-gray-900 mb-4">Tax Calculation Results</h4>
                      
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Base Amount:</span>
                          <span className="text-sm font-medium text-gray-900">
                            {formatCurrency(taxCalculation?.baseAmount)}
                          </span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Tax Rate:</span>
                          <span className="text-sm font-medium text-gray-900">
                            {formatPercentage(taxCalculation?.taxRate)}
                          </span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Tax Amount:</span>
                          <span className="text-sm font-medium text-gray-900">
                            {formatCurrency(taxCalculation?.taxAmount)}
                          </span>
                        </div>
                        
                        <div className="border-t border-gray-200 pt-3">
                          <div className="flex justify-between">
                            <span className="text-base font-medium text-gray-900">Total Amount:</span>
                            <span className="text-base font-bold text-gray-900">
                              {formatCurrency(taxCalculation?.totalAmount)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {taxCalculation?.applicableRates?.length > 0 && (
                        <div className="mt-6">
                          <h5 className="text-sm font-medium text-gray-900 mb-2">Applicable Tax Rates:</h5>
                          <div className="space-y-2">
                            {taxCalculation?.applicableRates?.map((rate) => (
                              <div key={rate?.id} className="flex justify-between text-xs">
                                <span className="text-gray-600">{rate?.taxName}:</span>
                                <span className="text-gray-900">{formatPercentage(rate?.taxRate)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Rate Optimization */}
          {activeFeature === 'optimization' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Rate Optimization</h3>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                <span className="text-4xl mb-4 block">🚧</span>
                <h4 className="text-lg font-medium text-yellow-800 mb-2">Coming Soon</h4>
                <p className="text-sm text-yellow-700">
                  AI-powered tax rate optimization recommendations will be available in a future update.
                </p>
              </div>
            </div>
          )}

          {/* Compliance Check */}
          {activeFeature === 'compliance' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Compliance Check</h3>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
                <span className="text-4xl mb-4 block">🔍</span>
                <h4 className="text-lg font-medium text-blue-800 mb-2">Coming Soon</h4>
                <p className="text-sm text-blue-700">
                  Automated compliance checking and audit trail analysis will be available in a future update.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AITaxAssistant;